/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */
/********************************************************/
/* ATTENTION: THIS FILE IS MOSTLY AI GENERATED AND      */
/* SHOULD ONLY BE USED TO SHOW HOW THE API ENDPOINTS    */
/* ARE USED AND WORK TOGETHER. IT DOES NOT REPRESENT    */
/* THE CODESTYLE OF THE PROJECT.                        */
/********************************************************/
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  SelectChangeEvent,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

import PageContentContainer from "../../Container/PageContentContainer";
import PageHeadline from "../../PageHeadline/PageHeadline";
import ContentBox from "../../ContentBox/ContentBox";
import ButtonLoading from "../../Button/ButtonLoading";
import ApiClient from "../../../services/ApiClient";
import ValidatorSelect from "../../Validator/ValidatorSelect";

type BankAccount = {
  id: string | null;
  slug: string;
  iban: string;
  bic: string;
  currency: string;
  balance: number;
  balanceCents: number;
  authorizedBalance: number;
  authorizedBalanceCents: number;
  name: string;
  updatedAt: string;
  status: string;
  main: boolean;
};

type Organization = {
  id: string;
  slug: string;
  legalName: string;
  legalNumber: string;
  legalSector: string;
  legalRegistrationDate: string;
  legalAddress: string;
  legalForm: string;
  legalCountry: string;
  legalShareCapital: number;
  locale: string;
  contractSignedAt: string;
  bankAccounts: BankAccount[];
};

type QontoTransaction = {
  organization: string;
  id: string;
  transactionId: string;
  amount: number;
  amountCents: number;
  settledBalance: number | null;
  settledBalanceCents: number | null;
  attachmentIds: string[];
  localAmount: number | null;
  localAmountCents: number | null;
  side: "credit" | "debit";
  operationType: string;
  currency: string;
  localCurrency: string | null;
  label: string | null;
  settledAt: string | null;
  emittedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  status: "pending" | "declined" | "completed" | string;
  note: string | null;
  reference: string | null;
  vatAmount: number | null;
  vatAmountCents: number | null;
  vatRate: number | null;
  initiatorId: string | null;
  labelIds: string[];
  attachmentLost: boolean | null;
  attachmentRequired: boolean | null;
  cardLastDigits: string | null;
  category: string | null;
  cashflowCategoryName: string | null;
  cashflowSubcategoryName: string | null;
  attachments: unknown[];
  labels: unknown[] | null;
  vatDetails: unknown[] | null;
  subjectType: string | null;
  transfer: unknown | null;
};

type SepaBeneficiaryStatus = "pending" | "validated" | "declined" | string;

type QontoSepaBeneficiary = {
  organization: string;
  id: string;
  name: string;
  iban: string;
  bic: string;
  status: SepaBeneficiaryStatus;
  trusted: boolean;
  createdAt: string;
  updatedAt: string;
  email: string | null;
  activityTag: string | null;
};

type HydraCollection<T> = {
  "hydra:member"?: T[];
  member?: T[];
  "hydra:totalItems"?: number;
  totalItems?: number;
};

type InternalTransferFormValues = {
  debitIban: string;
  creditIban: string;
  amount: string;
  reference: string;
};

type SepaBeneficiaryFormValues = {
  name: string;
  iban: string;
  bic: string;
  email: string;
  activityTag: string;
};

type TrustedFilter = "all" | "trusted" | "untrusted";

function getBankAccountValue(bankAccount: BankAccount): string {
  return bankAccount.id ?? bankAccount.slug;
}

function getHydraItems<T>(response: unknown): T[] {
  if (Array.isArray(response)) {
    return response as T[];
  }

  if (!response || typeof response !== "object") {
    return [];
  }

  const hydraResponse = response as HydraCollection<T>;

  return hydraResponse["hydra:member"] ?? hydraResponse.member ?? [];
}

function getHydraTotalItems(response: unknown): number {
  if (Array.isArray(response)) {
    return response.length;
  }

  if (!response || typeof response !== "object") {
    return 0;
  }

  const hydraResponse = response as HydraCollection<unknown>;

  return hydraResponse["hydra:totalItems"] ?? hydraResponse.totalItems ?? getHydraItems(response).length;
}

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency,
  }).format(amount);
}

function formatDateTime(value?: string | null): string {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("de-DE", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function getTransactionDescription(transaction: QontoTransaction): string {
  return (
    transaction.label ||
    transaction.reference ||
    transaction.note ||
    transaction.cashflowSubcategoryName ||
    transaction.cashflowCategoryName ||
    transaction.category ||
    transaction.operationType ||
    "-"
  );
}

function getSepaBeneficiaryStatusColor(status: SepaBeneficiaryStatus): "success" | "warning" | "error" | "default" {
  switch (status) {
    case "validated":
      return "success";
    case "pending":
      return "warning";
    case "declined":
      return "error";
    default:
      return "default";
  }
}

function getSepaBeneficiaryStatusLabel(status: SepaBeneficiaryStatus): string {
  switch (status) {
    case "validated":
      return "Validiert";
    case "pending":
      return "Ausstehend";
    case "declined":
      return "Abgelehnt";
    default:
      return status;
  }
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function InfoItem({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" sx={{
        color: "text.secondary"
      }}>
        {label}
      </Typography>
      <Typography variant="body1">{value || "-"}</Typography>
    </Box>
  );
}

type InternalTransferDialogProps = {
  open: boolean;
  organization: string;
  bankAccounts: BankAccount[];
  defaultDebitIban: string;
  onClose: () => void;
  onSuccess: (message: string) => void;
};

function InternalTransferDialog({
  open,
  organization,
  bankAccounts,
  defaultDebitIban,
  onClose,
  onSuccess,
}: InternalTransferDialogProps) {
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(null);

  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<InternalTransferFormValues>({
    defaultValues: {
      debitIban: defaultDebitIban,
      creditIban: "",
      amount: "",
      reference: "",
    },
  });

  const debitIban = watch("debitIban");
  const creditIban = watch("creditIban");

  // Reset form whenever the dialog (re-)opens so we always start with the
  // currently selected bank account as the default debit account.
  useEffect(() => {
    if (open) {
      reset({
        debitIban: defaultDebitIban,
        creditIban: "",
        amount: "",
        reference: "",
      });
      setSubmitErrorMessage(null);
    }
  }, [open, defaultDebitIban, reset]);

  // If the user changes the source so it equals the currently picked target,
  // clear the target so they have to consciously pick a new one.
  useEffect(() => {
    if (creditIban && creditIban === debitIban) {
      setValue("creditIban", "");
    }
  }, [debitIban, creditIban, setValue]);

  // Only accounts that actually have an IBAN can be used for an internal transfer.
  const transferableAccounts = useMemo(
    () =>
      bankAccounts.filter(
        (bankAccount) =>
          bankAccount.iban && bankAccount.iban.trim() !== "" && bankAccount.status.toLowerCase() === "active"
      ),
    [bankAccounts]
  );

  const creditAccountOptions = useMemo(
    () => transferableAccounts.filter((bankAccount) => bankAccount.iban !== debitIban),
    [transferableAccounts, debitIban]
  );

  const debitAccount = useMemo(
    () => transferableAccounts.find((bankAccount) => bankAccount.iban === debitIban) ?? null,
    [transferableAccounts, debitIban]
  );

  const currency = debitAccount?.currency ?? "EUR";
  const hasEnoughAccounts = transferableAccounts.length >= 2;

  const onSubmit = async (values: InternalTransferFormValues) => {
    setSubmitErrorMessage(null);

    const normalizedAmount = values.amount.trim().replace(",", ".");

    try {
      await ApiClient.post(`/lb/qonto/${organization}/internal-transfers`, {
        body: JSON.stringify({
          debitIban: values.debitIban,
          creditIban: values.creditIban,
          amount: normalizedAmount,
          currency,
          reference: values.reference.trim(),
        }),
      });

      onSuccess("Die interne Überweisung wurde erfolgreich angelegt.");
      onClose();
    } catch (error) {
      console.error(error);
      setSubmitErrorMessage("Die interne Überweisung konnte nicht angelegt werden.");
    }
  };

  const renderBankAccountMenuItem = (bankAccount: BankAccount) => (
    <MenuItem key={bankAccount.iban} value={bankAccount.iban}>
      {bankAccount.name} · {bankAccount.iban}
      {bankAccount.main ? " · Hauptkonto" : ""}
    </MenuItem>
  );

  const handleDialogClose = () => {
    if (isSubmitting) {
      return;
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>Interne Überweisung anlegen</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {!hasEnoughAccounts && (
            <Alert severity="info">
              Für eine interne Überweisung werden mindestens zwei Bankkonten mit IBAN benötigt.
            </Alert>
          )}

          <Box>
            <Controller
              name="debitIban"
              control={control}
              rules={{ required: "Quellkonto ist erforderlich" }}
              render={({ field, fieldState }) => (
                <ValidatorSelect
                  {...field}
                  label="Von Konto (Quelle)"
                  disabled={isSubmitting || transferableAccounts.length === 0}
                  error={Boolean(fieldState.error)}
                >
                  {transferableAccounts.map(renderBankAccountMenuItem)}
                </ValidatorSelect>
              )}
            />
            {errors.debitIban?.message && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                {errors.debitIban.message}
              </Typography>
            )}
            {debitAccount && (
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                  display: "block"
                }}>
                Verfügbarer Saldo: {formatCurrency(debitAccount.authorizedBalance, debitAccount.currency)}
              </Typography>
            )}
          </Box>

          <Box>
            <Controller
              name="creditIban"
              control={control}
              rules={{
                required: "Zielkonto ist erforderlich",
                validate: (value) => value !== debitIban || "Ziel- und Quellkonto müssen unterschiedlich sein",
              }}
              render={({ field, fieldState }) => (
                <ValidatorSelect
                  {...field}
                  label="Auf Konto (Ziel)"
                  disabled={isSubmitting || creditAccountOptions.length === 0}
                  error={Boolean(fieldState.error)}
                >
                  {creditAccountOptions.map(renderBankAccountMenuItem)}
                </ValidatorSelect>
              )}
            />
            {errors.creditIban?.message && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                {errors.creditIban.message}
              </Typography>
            )}
          </Box>

          <TextField
            label="Betrag"
            type="text"
            inputMode="decimal"
            fullWidth
            disabled={isSubmitting}
            error={Boolean(errors.amount)}
            helperText={errors.amount?.message ?? `Wird in ${currency} gebucht.`}
            {...register("amount", {
              required: "Betrag ist erforderlich",
              validate: (value) => {
                const normalized = value.trim().replace(",", ".");
                if (!/^\d+(\.\d{1,2})?$/.test(normalized)) {
                  return "Bitte einen gültigen Betrag eingeben (z. B. 100,00).";
                }
                const numeric = Number(normalized);
                if (Number.isNaN(numeric) || numeric <= 0) {
                  return "Betrag muss größer als 0 sein.";
                }
                return true;
              },
            })}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">{currency}</InputAdornment>,
              }
            }} />

          <TextField
            label="Referenz"
            fullWidth
            disabled={isSubmitting}
            error={Boolean(errors.reference)}
            helperText={errors.reference?.message ?? "Wird auf der Überweisung angezeigt (max. 99 Zeichen)."}
            {...register("reference", {
              required: "Referenz ist erforderlich",
              maxLength: { value: 99, message: "Referenz darf maximal 99 Zeichen lang sein." },
              validate: (value) => value.trim().length > 0 || "Referenz darf nicht nur aus Leerzeichen bestehen.",
            })}
            slotProps={{
              htmlInput: { maxLength: 99 }
            }} />

          {submitErrorMessage && <Alert severity="error">{submitErrorMessage}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose} disabled={isSubmitting}>
          Abbrechen
        </Button>
        <ButtonLoading
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          isLoading={isSubmitting}
          disabled={isSubmitting || !hasEnoughAccounts}
        >
          Überweisung anlegen
        </ButtonLoading>
      </DialogActions>
    </Dialog>
  );
}

type SepaBeneficiaryDialogProps = {
  open: boolean;
  organization: string;
  // null = create a new beneficiary, otherwise edit the given one.
  beneficiary: QontoSepaBeneficiary | null;
  onClose: () => void;
  onSuccess: (message: string) => void;
};

function SepaBeneficiaryDialog({ open, organization, beneficiary, onClose, onSuccess }: SepaBeneficiaryDialogProps) {
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(null);

  const isEditMode = beneficiary !== null;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SepaBeneficiaryFormValues>({
    defaultValues: {
      name: "",
      iban: "",
      bic: "",
      email: "",
      activityTag: "",
    },
  });

  // Re-initialise the form whenever the dialog opens or we switch between
  // editing different beneficiaries. Otherwise stale values from the previous
  // open would leak into the next session.
  useEffect(() => {
    if (!open) {
      return;
    }

    if (beneficiary) {
      reset({
        name: beneficiary.name ?? "",
        iban: beneficiary.iban ?? "",
        bic: beneficiary.bic ?? "",
        email: beneficiary.email ?? "",
        activityTag: beneficiary.activityTag ?? "",
      });
    } else {
      reset({
        name: "",
        iban: "",
        bic: "",
        email: "",
        activityTag: "",
      });
    }

    setSubmitErrorMessage(null);
  }, [open, beneficiary, reset]);

  const onSubmit = async (values: SepaBeneficiaryFormValues) => {
    setSubmitErrorMessage(null);

    const normalizedName = values.name.trim();
    const normalizedEmail = values.email.trim();
    const normalizedActivityTag = values.activityTag.trim();

    try {
      if (isEditMode && beneficiary) {
        // PATCH: Qonto only accepts name, email and activity_tag.
        await ApiClient.patch(`/lb/qonto/${organization}/sepa-beneficiaries/${beneficiary.id}`, {
          body: JSON.stringify({
            name: normalizedName,
            // Empty string -> null so a value can actually be cleared on Qonto's side.
            email: normalizedEmail === "" ? null : normalizedEmail,
            activityTag: normalizedActivityTag === "" ? null : normalizedActivityTag,
          }),
        });

        onSuccess("Der Begünstigte wurde erfolgreich aktualisiert.");
      } else {
        // POST: name, iban, bic are required; email and activity_tag are optional.
        await ApiClient.post(`/lb/qonto/${organization}/sepa-beneficiaries`, {
          body: JSON.stringify({
            name: normalizedName,
            iban: values.iban.trim().toUpperCase().replace(/\s+/g, ""),
            bic: values.bic.trim().toUpperCase().replace(/\s+/g, ""),
            email: normalizedEmail === "" ? null : normalizedEmail,
            activityTag: normalizedActivityTag === "" ? null : normalizedActivityTag,
          }),
        });

        onSuccess("Der Begünstigte wurde erfolgreich angelegt.");
      }

      onClose();
    } catch (error) {
      console.error(error);

      setSubmitErrorMessage(
        isEditMode
          ? "Der Begünstigte konnte nicht aktualisiert werden."
          : "Der Begünstigte konnte nicht angelegt werden."
      );
    }
  };

  const handleDialogClose = () => {
    if (isSubmitting) {
      return;
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditMode ? "SEPA-Begünstigten bearbeiten" : "SEPA-Begünstigten anlegen"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {isEditMode && (
            <Alert severity="info">
              IBAN und BIC können nach dem Anlegen nicht mehr geändert werden. Möchtest du diese ändern, lege bitte
              einen neuen Begünstigten an.
            </Alert>
          )}

          <TextField
            label="Name"
            fullWidth
            disabled={isSubmitting}
            error={Boolean(errors.name)}
            helperText={errors.name?.message ?? "Name des Begünstigten (z. B. Firma oder Person)."}
            {...register("name", {
              required: "Name ist erforderlich",
              validate: (value) => value.trim().length > 0 || "Name darf nicht nur aus Leerzeichen bestehen.",
            })}
            slotProps={{
              htmlInput: { maxLength: 100 }
            }} />

          <TextField
            label="IBAN"
            fullWidth
            disabled={isSubmitting || isEditMode}
            error={Boolean(errors.iban)}
            helperText={
              errors.iban?.message ??
              (isEditMode ? "Im Bearbeiten-Modus nicht änderbar." : "z. B. DE89 3704 0044 0532 0130 00")
            }
            {...register("iban", {
              required: isEditMode ? false : "IBAN ist erforderlich",
              validate: (value) => {
                if (isEditMode) {
                  return true;
                }
                const cleaned = value.replace(/\s+/g, "").toUpperCase();
                if (cleaned.length < 15 || cleaned.length > 34) {
                  return "IBAN muss zwischen 15 und 34 Zeichen lang sein.";
                }
                if (!/^[A-Z0-9]+$/.test(cleaned)) {
                  return "IBAN darf nur Buchstaben und Ziffern enthalten.";
                }
                return true;
              },
            })}
          />

          <TextField
            label="BIC"
            fullWidth
            disabled={isSubmitting || isEditMode}
            error={Boolean(errors.bic)}
            helperText={
              errors.bic?.message ??
              (isEditMode ? "Im Bearbeiten-Modus nicht änderbar." : "8 oder 11 Zeichen, z. B. COBADEFFXXX.")
            }
            {...register("bic", {
              required: isEditMode ? false : "BIC ist erforderlich",
              validate: (value) => {
                if (isEditMode) {
                  return true;
                }
                const cleaned = value.replace(/\s+/g, "").toUpperCase();
                if (cleaned.length !== 8 && cleaned.length !== 11) {
                  return "BIC muss 8 oder 11 Zeichen lang sein.";
                }
                if (!/^[A-Z0-9]+$/.test(cleaned)) {
                  return "BIC darf nur Buchstaben und Ziffern enthalten.";
                }
                return true;
              },
            })}
          />

          <TextField
            label="E-Mail (optional)"
            type="email"
            fullWidth
            disabled={isSubmitting}
            error={Boolean(errors.email)}
            helperText={errors.email?.message ?? "Wird für Benachrichtigungen verwendet."}
            {...register("email", {
              validate: (value) => {
                const trimmed = value.trim();
                if (trimmed === "") {
                  return true;
                }
                return EMAIL_REGEX.test(trimmed) || "Bitte eine gültige E-Mail-Adresse eingeben.";
              },
            })}
          />

          <TextField
            label="Activity Tag (optional)"
            fullWidth
            disabled={isSubmitting}
            error={Boolean(errors.activityTag)}
            helperText={
              errors.activityTag?.message ??
              "Kategorie für die Buchhaltung, z. B. supplier, salary, tax, utility, restaurant."
            }
            {...register("activityTag")}
          />

          {submitErrorMessage && <Alert severity="error">{submitErrorMessage}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose} disabled={isSubmitting}>
          Abbrechen
        </Button>
        <ButtonLoading
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          isLoading={isSubmitting}
          disabled={isSubmitting}
        >
          {isEditMode ? "Speichern" : "Anlegen"}
        </ButtonLoading>
      </DialogActions>
    </Dialog>
  );
}

export default function QontoTest() {
  const [organization, setOrganization] = useState("");
  const [organizationData, setOrganizationData] = useState<Organization | null>(null);
  const [selectedBankAccountValue, setSelectedBankAccountValue] = useState("");

  const [transactions, setTransactions] = useState<QontoTransaction[]>([]);
  const [transactionTotalItems, setTransactionTotalItems] = useState(0);
  const [transactionPage, setTransactionPage] = useState(0);
  const [transactionsPerPage, setTransactionsPerPage] = useState(50);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [transactionErrorMessage, setTransactionErrorMessage] = useState<string | null>(null);

  const [isCheckingConnection, setIsCheckingConnection] = useState(false);
  const [isLoadingOrganization, setIsLoadingOrganization] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);

  const [reloadCounter, setReloadCounter] = useState(0);
  const [transactionsReloadCounter, setTransactionsReloadCounter] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [isTransferDialogOpen, setIsTransferDialogOpen] = useState(false);

  // SEPA beneficiaries
  const [beneficiaries, setBeneficiaries] = useState<QontoSepaBeneficiary[]>([]);
  const [beneficiariesTotalItems, setBeneficiariesTotalItems] = useState(0);
  const [beneficiariesPage, setBeneficiariesPage] = useState(0);
  const [beneficiariesPerPage, setBeneficiariesPerPage] = useState(25);
  const [beneficiariesStatusFilter, setBeneficiariesStatusFilter] = useState<string>("all");
  const [beneficiariesTrustedFilter, setBeneficiariesTrustedFilter] = useState<TrustedFilter>("all");
  const [isLoadingBeneficiaries, setIsLoadingBeneficiaries] = useState(false);
  const [beneficiariesErrorMessage, setBeneficiariesErrorMessage] = useState<string | null>(null);
  const [beneficiariesReloadCounter, setBeneficiariesReloadCounter] = useState(0);

  const [isBeneficiaryDialogOpen, setIsBeneficiaryDialogOpen] = useState(false);
  const [editingBeneficiary, setEditingBeneficiary] = useState<QontoSepaBeneficiary | null>(null);

  // Single shared snackbar for all positive feedback (transfer + beneficiary actions).
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const bankAccounts = organizationData?.bankAccounts ?? [];

  const selectedBankAccount = useMemo(() => {
    return bankAccounts.find((bankAccount) => getBankAccountValue(bankAccount) === selectedBankAccountValue) ?? null;
  }, [bankAccounts, selectedBankAccountValue]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data?.type !== "QONTO_OAUTH_DONE") {
        return;
      }

      setIsConnecting(false);
      setReloadCounter((currentValue) => currentValue + 1);
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  useEffect(() => {
    let isActive = true;

    const resetOrganizationData = () => {
      setIsConnected(false);
      setOrganizationData(null);
      setSelectedBankAccountValue("");
      setTransactions([]);
      setTransactionTotalItems(0);
      setTransactionPage(0);
      setTransactionErrorMessage(null);
      setIsLoadingTransactions(false);
      setBeneficiaries([]);
      setBeneficiariesTotalItems(0);
      setBeneficiariesPage(0);
      setBeneficiariesErrorMessage(null);
      setIsLoadingBeneficiaries(false);
      setErrorMessage(null);
    };

    const getConnectionStatus = async (selectedOrganization: string): Promise<boolean> => {
      const responseStatus = await ApiClient.get(`/lb/services/qonto/status/${selectedOrganization}`);

      return responseStatus?.connected === true;
    };

    const loadOrganization = async (selectedOrganization: string) => {
      setIsLoadingOrganization(true);

      const responseOrganization = await ApiClient.get(`/lb/qonto/${selectedOrganization}/organization`);

      if (!isActive) {
        return;
      }

      const loadedOrganization = responseOrganization as Organization;
      const loadedBankAccounts = loadedOrganization.bankAccounts ?? [];
      const defaultBankAccount = loadedBankAccounts.find((bankAccount) => bankAccount.main) ?? loadedBankAccounts[0];

      setOrganizationData({
        ...loadedOrganization,
        bankAccounts: loadedBankAccounts,
      });

      setSelectedBankAccountValue(defaultBankAccount ? getBankAccountValue(defaultBankAccount) : "");
      setIsLoadingOrganization(false);
    };

    const loadQontoData = async () => {
      resetOrganizationData();

      if (!organization) {
        return;
      }

      setIsCheckingConnection(true);

      try {
        const connected = await getConnectionStatus(organization);

        if (!isActive) {
          return;
        }

        setIsConnected(connected);
        setIsCheckingConnection(false);

        if (!connected) {
          return;
        }

        await loadOrganization(organization);
      } catch (error) {
        if (!isActive) {
          return;
        }

        console.error(error);

        setIsConnected(false);
        setOrganizationData(null);
        setSelectedBankAccountValue("");
        setTransactions([]);
        setTransactionTotalItems(0);
        setTransactionErrorMessage(null);
        setBeneficiaries([]);
        setBeneficiariesTotalItems(0);
        setBeneficiariesErrorMessage(null);
        setErrorMessage("Die Qonto-Daten konnten nicht geladen werden.");
        setIsCheckingConnection(false);
        setIsLoadingOrganization(false);
        setIsLoadingTransactions(false);
        setIsLoadingBeneficiaries(false);
      }
    };

    loadQontoData();

    return () => {
      isActive = false;
    };
  }, [organization, reloadCounter]);

  useEffect(() => {
    let isActive = true;

    const loadTransactions = async () => {
      setTransactions([]);
      setTransactionTotalItems(0);
      setTransactionErrorMessage(null);

      if (!organization || !isConnected || !selectedBankAccount) {
        return;
      }

      if (!selectedBankAccount.id) {
        setTransactionErrorMessage("Für dieses Bankkonto fehlt die bankAccountId.");
        return;
      }

      setIsLoadingTransactions(true);

      try {
        const searchParams = new URLSearchParams({
          bankAccountId: selectedBankAccount.id,
          sortBy: "settled_at:desc",
          page: String(transactionPage + 1),
          perPage: String(transactionsPerPage),
        });

        const responseTransactions = await ApiClient.get(
          `/lb/qonto/${organization}/transactions?${searchParams.toString()}`
        );

        if (!isActive) {
          return;
        }

        setTransactions(getHydraItems<QontoTransaction>(responseTransactions));
        setTransactionTotalItems(getHydraTotalItems(responseTransactions));
      } catch (error) {
        if (!isActive) {
          return;
        }

        console.error(error);

        setTransactions([]);
        setTransactionTotalItems(0);
        setTransactionErrorMessage("Die Transaktionen konnten nicht geladen werden.");
      } finally {
        if (isActive) {
          setIsLoadingTransactions(false);
        }
      }
    };

    loadTransactions();

    return () => {
      isActive = false;
    };
  }, [organization, isConnected, selectedBankAccount, transactionPage, transactionsPerPage, transactionsReloadCounter]);

  useEffect(() => {
    let isActive = true;

    const loadBeneficiaries = async () => {
      setBeneficiaries([]);
      setBeneficiariesTotalItems(0);
      setBeneficiariesErrorMessage(null);

      if (!organization || !isConnected) {
        return;
      }

      setIsLoadingBeneficiaries(true);

      try {
        const searchParams = new URLSearchParams({
          sortBy: "updated_at:desc",
          page: String(beneficiariesPage + 1),
          perPage: String(beneficiariesPerPage),
        });

        if (beneficiariesStatusFilter !== "all") {
          // Backend expects an array param "statuses[]".
          searchParams.append("statuses[]", beneficiariesStatusFilter);
        }

        if (beneficiariesTrustedFilter !== "all") {
          searchParams.append("trusted", beneficiariesTrustedFilter === "trusted" ? "true" : "false");
        }

        const responseBeneficiaries = await ApiClient.get(
          `/lb/qonto/${organization}/sepa-beneficiaries?${searchParams.toString()}`
        );

        if (!isActive) {
          return;
        }

        setBeneficiaries(getHydraItems<QontoSepaBeneficiary>(responseBeneficiaries));
        setBeneficiariesTotalItems(getHydraTotalItems(responseBeneficiaries));
      } catch (error) {
        if (!isActive) {
          return;
        }

        console.error(error);

        setBeneficiaries([]);
        setBeneficiariesTotalItems(0);
        setBeneficiariesErrorMessage("Die SEPA-Begünstigten konnten nicht geladen werden.");
      } finally {
        if (isActive) {
          setIsLoadingBeneficiaries(false);
        }
      }
    };

    loadBeneficiaries();

    return () => {
      isActive = false;
    };
  }, [
    organization,
    isConnected,
    beneficiariesPage,
    beneficiariesPerPage,
    beneficiariesStatusFilter,
    beneficiariesTrustedFilter,
    beneficiariesReloadCounter,
  ]);

  const getCenteredPopupFeatures = (width: number, height: number): string => {
    const left = window.screenX + Math.max((window.outerWidth - width) / 2, 0);
    const top = window.screenY + Math.max((window.outerHeight - height) / 2, 0);

    return [
      `width=${width}`,
      `height=${height}`,
      `left=${Math.round(left)}`,
      `top=${Math.round(top)}`,
      "resizable=yes",
      "scrollbars=yes",
    ].join(",");
  };

  const login = async () => {
    if (!organization) {
      return;
    }

    setIsConnecting(true);
    setErrorMessage(null);

    const popupWidth = 1200;
    const popupHeight = 800;

    const popup = window.open("about:blank", "qontoOAuth", getCenteredPopupFeatures(popupWidth, popupHeight));

    try {
      const responseConnect = await ApiClient.get(`/lb/services/qonto/connect/${organization}`);
      const authorizationUrl = responseConnect?.AuthorizationUrl;

      if (!popup || !authorizationUrl) {
        throw new Error("Qonto authorization URL could not be opened.");
      }

      popup.location.href = authorizationUrl;
      popup.focus();
    } catch (error) {
      console.error(error);

      popup?.close();
      setIsConnecting(false);
      setErrorMessage("Der Qonto-Login konnte nicht gestartet werden.");
    }
  };

  const handleOrganizationChange = (event: SelectChangeEvent) => {
    setOrganization(event.target.value);
    setTransactionPage(0);
    setBeneficiariesPage(0);
  };

  const handleBankAccountChange = (event: SelectChangeEvent) => {
    setSelectedBankAccountValue(event.target.value);
    setTransactionPage(0);
  };

  const handleTransactionPageChange = (_event: unknown, newPage: number) => {
    setTransactionPage(newPage);
  };

  const handleTransactionsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTransactionsPerPage(Number(event.target.value));
    setTransactionPage(0);
  };

  const handleBeneficiariesPageChange = (_event: unknown, newPage: number) => {
    setBeneficiariesPage(newPage);
  };

  const handleBeneficiariesPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setBeneficiariesPerPage(Number(event.target.value));
    setBeneficiariesPage(0);
  };

  const handleBeneficiariesStatusFilterChange = (event: SelectChangeEvent) => {
    setBeneficiariesStatusFilter(event.target.value);
    setBeneficiariesPage(0);
  };

  const handleBeneficiariesTrustedFilterChange = (event: SelectChangeEvent) => {
    setBeneficiariesTrustedFilter(event.target.value as TrustedFilter);
    setBeneficiariesPage(0);
  };

  const handleTransferSuccess = (message: string) => {
    setSuccessMessage(message);
    setTransactionsReloadCounter((currentValue) => currentValue + 1);
  };

  const handleBeneficiarySuccess = (message: string) => {
    setSuccessMessage(message);
    setBeneficiariesReloadCounter((currentValue) => currentValue + 1);
  };

  const openCreateBeneficiaryDialog = () => {
    setEditingBeneficiary(null);
    setIsBeneficiaryDialogOpen(true);
  };

  const openEditBeneficiaryDialog = (beneficiary: QontoSepaBeneficiary) => {
    setEditingBeneficiary(beneficiary);
    setIsBeneficiaryDialogOpen(true);
  };

  const closeBeneficiaryDialog = () => {
    setIsBeneficiaryDialogOpen(false);
    // Keep the editingBeneficiary around briefly so the dialog doesn't visibly
    // flicker its title/contents during the close animation.
  };

  const transferableAccountsCount = bankAccounts.filter(
    (bankAccount) => bankAccount.iban && bankAccount.iban.trim() !== ""
  ).length;

  const canOpenTransferDialog = isConnected && !isLoadingOrganization && transferableAccountsCount >= 2;

  // The default debit IBAN: prefer the currently selected bank account, otherwise
  // fall back to the main / first transferable account so the dialog opens with
  // a sensible pre-selection even when no account has been chosen yet.
  const defaultDebitIban = useMemo(() => {
    if (selectedBankAccount?.iban) {
      return selectedBankAccount.iban;
    }

    const fallback =
      bankAccounts.find((bankAccount) => bankAccount.main && bankAccount.iban) ??
      bankAccounts.find((bankAccount) => bankAccount.iban);

    return fallback?.iban ?? "";
  }, [selectedBankAccount, bankAccounts]);

  return (
    <PageContentContainer>
      <PageHeadline main="💰💵💰💵💰 Qonto Test Page 💰💵💰💵💰" />
      <ContentBox headline="Führ mich zum Schotter!">
        <Grid container spacing={2} sx={{
          alignItems: "center"
        }}>
          <Grid
            size={{
              xs: 12,
              md: 3
            }}>
            <ValidatorSelect label="Organisation" value={organization} onChange={handleOrganizationChange}>
              <MenuItem value="legalbird">Legalbird</MenuItem>
              <MenuItem value="lawfirm">Kanzlei</MenuItem>
            </ValidatorSelect>
          </Grid>

          {organization && isConnected && bankAccounts.length > 0 && (
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <ValidatorSelect
                label="Bankkonto"
                value={selectedBankAccountValue}
                onChange={handleBankAccountChange}
                disabled={isLoadingOrganization}
              >
                {bankAccounts.map((bankAccount) => (
                  <MenuItem key={getBankAccountValue(bankAccount)} value={getBankAccountValue(bankAccount)}>
                    {bankAccount.name} · {bankAccount.iban}
                    {bankAccount.main ? " · Hauptkonto" : ""}
                  </MenuItem>
                ))}
              </ValidatorSelect>
            </Grid>
          )}

          {organization && isCheckingConnection && (
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Stack direction="row" spacing={1} sx={{
                alignItems: "center"
              }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Verbindung wird geprüft...</Typography>
              </Stack>
            </Grid>
          )}

          {organization && !isCheckingConnection && !isConnected && (
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <ButtonLoading variant="contained" onClick={login} isLoading={isConnecting} disabled={isConnecting}>
                Login
              </ButtonLoading>
            </Grid>
          )}
        </Grid>

        {errorMessage && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage}
          </Alert>
        )}

        {organization && isConnected && isLoadingOrganization && (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
              mt: 2
            }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Organization wird geladen...</Typography>
          </Stack>
        )}

        {organization && isConnected && !isLoadingOrganization && organizationData && bankAccounts.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Für diese Organization wurden keine Bankkonten gefunden.
          </Alert>
        )}
      </ContentBox>
      {organizationData && (
        <ContentBox headline="Organization">
          <Grid container spacing={3}>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <InfoItem label="Name" value={organizationData.legalName} />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <InfoItem label="Slug" value={organizationData.slug} />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <InfoItem label="Legal Number" value={organizationData.legalNumber} />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <InfoItem label="Legal Form" value={organizationData.legalForm} />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <InfoItem label="Legal Sector" value={organizationData.legalSector} />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <InfoItem label="Legal Country" value={organizationData.legalCountry} />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <InfoItem label="Legal Registration Date" value={organizationData.legalRegistrationDate} />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <InfoItem label="Contract Signed At" value={organizationData.contractSignedAt} />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <InfoItem label="Share Capital" value={formatCurrency(organizationData.legalShareCapital, "EUR")} />
            </Grid>

            <Grid size={12}>
              <InfoItem label="Address" value={organizationData.legalAddress} />
            </Grid>
          </Grid>

          {selectedBankAccount && (
            <>
              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" sx={{ mb: 2 }}>
                Ausgewähltes Bankkonto
              </Typography>

              <Grid container spacing={3}>
                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <InfoItem label="Name" value={selectedBankAccount.name} />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <InfoItem label="IBAN" value={selectedBankAccount.iban} />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <InfoItem label="BIC" value={selectedBankAccount.bic} />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <InfoItem
                    label="Balance"
                    value={formatCurrency(selectedBankAccount.balance, selectedBankAccount.currency)}
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <InfoItem
                    label="Authorized Balance"
                    value={formatCurrency(selectedBankAccount.authorizedBalance, selectedBankAccount.currency)}
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <InfoItem label="Status" value={selectedBankAccount.status} />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <InfoItem label="Main Account" value={selectedBankAccount.main ? "Ja" : "Nein"} />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <InfoItem label="Updated At" value={selectedBankAccount.updatedAt} />
                </Grid>
              </Grid>
            </>
          )}

          <Divider sx={{ my: 3 }} />

          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: "center",
              flexWrap: "wrap"
            }}>
            <Button variant="contained" onClick={() => setIsTransferDialogOpen(true)} disabled={!canOpenTransferDialog}>
              Interne Überweisung anlegen
            </Button>

            {!canOpenTransferDialog && transferableAccountsCount < 2 && (
              <Typography variant="caption" sx={{
                color: "text.secondary"
              }}>
                Es werden mindestens zwei Bankkonten mit IBAN benötigt.
              </Typography>
            )}
          </Stack>
        </ContentBox>
      )}
      {organizationData && selectedBankAccount && (
        <ContentBox headline={`Transaktionen · ${selectedBankAccount.name}`}>
          {isLoadingTransactions && (
            <Stack direction="row" spacing={1} sx={{
              alignItems: "center"
            }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Transaktionen werden geladen...</Typography>
            </Stack>
          )}

          {transactionErrorMessage && <Alert severity="error">{transactionErrorMessage}</Alert>}

          {!isLoadingTransactions && !transactionErrorMessage && transactionTotalItems === 0 && (
            <Alert severity="info">Für dieses Bankkonto wurden keine Transaktionen gefunden.</Alert>
          )}

          {transactions.length > 0 && (
            <>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Datum</TableCell>
                      <TableCell>Beschreibung</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Typ</TableCell>
                      <TableCell>Kategorie</TableCell>
                      <TableCell align="right">Betrag</TableCell>
                      <TableCell align="right">Saldo</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {transactions.map((transaction) => {
                      const description = getTransactionDescription(transaction);
                      const amountColor =
                        transaction.side === "credit"
                          ? "success.main"
                          : transaction.side === "debit"
                          ? "error.main"
                          : "text.primary";

                      return (
                        <TableRow key={transaction.id}>
                          <TableCell>
                            {formatDateTime(transaction.settledAt ?? transaction.emittedAt ?? transaction.createdAt)}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{description}</Typography>

                            {transaction.reference && transaction.reference !== description && (
                              <Typography variant="caption" sx={{
                                color: "text.secondary"
                              }}>
                                {transaction.reference}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip label={transaction.status} size="small" />
                          </TableCell>
                          <TableCell>{transaction.operationType}</TableCell>
                          <TableCell>
                            {transaction.cashflowSubcategoryName ||
                              transaction.cashflowCategoryName ||
                              transaction.category ||
                              "-"}
                          </TableCell>
                          <TableCell align="right">
                            <Typography component="span" variant="body2" sx={{ color: amountColor, fontWeight: 600 }}>
                              {formatCurrency(transaction.amount, transaction.currency)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {transaction.settledBalance !== null
                              ? formatCurrency(transaction.settledBalance, transaction.currency)
                              : "-"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>

              <TablePagination
                component="div"
                count={transactionTotalItems}
                page={transactionPage}
                rowsPerPage={transactionsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                onPageChange={handleTransactionPageChange}
                onRowsPerPageChange={handleTransactionsPerPageChange}
                labelRowsPerPage="Transaktionen pro Seite"
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} von ${count}`}
              />
            </>
          )}
        </ContentBox>
      )}
      {organizationData && (
        <ContentBox headline="SEPA-Begünstigte">
          <Grid
            container
            spacing={2}
            sx={{
              alignItems: "center",
              mb: 2
            }}>
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <ValidatorSelect
                label="Status"
                value={beneficiariesStatusFilter}
                onChange={handleBeneficiariesStatusFilterChange}
                disabled={isLoadingBeneficiaries}
              >
                <MenuItem value="all">Alle</MenuItem>
                <MenuItem value="validated">Validiert</MenuItem>
                <MenuItem value="pending">Ausstehend</MenuItem>
                <MenuItem value="declined">Abgelehnt</MenuItem>
              </ValidatorSelect>
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <ValidatorSelect
                label="Vertrauenswürdig"
                value={beneficiariesTrustedFilter}
                onChange={handleBeneficiariesTrustedFilterChange}
                disabled={isLoadingBeneficiaries}
              >
                <MenuItem value="all">Alle</MenuItem>
                <MenuItem value="trusted">Nur vertrauenswürdig</MenuItem>
                <MenuItem value="untrusted">Nur nicht vertrauenswürdig</MenuItem>
              </ValidatorSelect>
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Stack direction="row" spacing={2} sx={{
                justifyContent: { xs: "flex-start", md: "flex-end" }
              }}>
                <Button
                  variant="outlined"
                  onClick={() => setBeneficiariesReloadCounter((currentValue) => currentValue + 1)}
                  disabled={isLoadingBeneficiaries}
                >
                  Neu laden
                </Button>
                <Button variant="contained" onClick={openCreateBeneficiaryDialog} disabled={!isConnected}>
                  Neuen Begünstigten anlegen
                </Button>
              </Stack>
            </Grid>
          </Grid>

          {isLoadingBeneficiaries && (
            <Stack direction="row" spacing={1} sx={{
              alignItems: "center"
            }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Begünstigte werden geladen...</Typography>
            </Stack>
          )}

          {beneficiariesErrorMessage && <Alert severity="error">{beneficiariesErrorMessage}</Alert>}

          {!isLoadingBeneficiaries && !beneficiariesErrorMessage && beneficiaries.length === 0 && (
            <Alert severity="info">Für die gewählten Filter wurden keine Begünstigten gefunden.</Alert>
          )}

          {beneficiaries.length > 0 && (
            <>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>IBAN</TableCell>
                      <TableCell>BIC</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Vertrauenswürdig</TableCell>
                      <TableCell>E-Mail</TableCell>
                      <TableCell>Activity Tag</TableCell>
                      <TableCell>Aktualisiert</TableCell>
                      <TableCell align="right">Aktionen</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {beneficiaries.map((beneficiary) => (
                      <TableRow key={beneficiary.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {beneficiary.name}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                            {beneficiary.iban}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                            {beneficiary.bic}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={getSepaBeneficiaryStatusLabel(beneficiary.status)}
                            size="small"
                            color={getSepaBeneficiaryStatusColor(beneficiary.status)}
                          />
                        </TableCell>

                        <TableCell>
                          {beneficiary.trusted ? (
                            <Chip label="Ja" size="small" color="success" variant="outlined" />
                          ) : (
                            <Chip label="Nein" size="small" variant="outlined" />
                          )}
                        </TableCell>

                        <TableCell>{beneficiary.email || "-"}</TableCell>

                        <TableCell>{beneficiary.activityTag || "-"}</TableCell>

                        <TableCell>{formatDateTime(beneficiary.updatedAt)}</TableCell>

                        <TableCell align="right">
                          <Tooltip title="Bearbeiten">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => openEditBeneficiaryDialog(beneficiary)}
                                disabled={!isConnected}
                                aria-label={`Begünstigten ${beneficiary.name} bearbeiten`}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              <TablePagination
                component="div"
                count={beneficiariesTotalItems}
                page={beneficiariesPage}
                rowsPerPage={beneficiariesPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                onPageChange={handleBeneficiariesPageChange}
                onRowsPerPageChange={handleBeneficiariesPerPageChange}
                labelRowsPerPage="Begünstigte pro Seite"
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} von ${count}`}
              />
            </>
          )}
        </ContentBox>
      )}
      {organizationData && (
        <InternalTransferDialog
          open={isTransferDialogOpen}
          organization={organization}
          bankAccounts={bankAccounts}
          defaultDebitIban={defaultDebitIban}
          onClose={() => setIsTransferDialogOpen(false)}
          onSuccess={handleTransferSuccess}
        />
      )}
      {organizationData && (
        <SepaBeneficiaryDialog
          open={isBeneficiaryDialogOpen}
          organization={organization}
          beneficiary={editingBeneficiary}
          onClose={closeBeneficiaryDialog}
          onSuccess={handleBeneficiarySuccess}
        />
      )}
      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setSuccessMessage(null)} sx={{ width: "100%" }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </PageContentContainer>
  );
}
